import React from 'react';
import { cn } from '@/lib/utils';

/**
 * Heading sizes from the approved package (brand guide "Typography" + style.css /
 * pages.css), in rem so they follow text enlargement. Mobile first; `nav` = 800px.
 * - display: home hero H1 (43–53px phones, 47–70px tablets, up to 118px) and the navy
 *   closing band; line-height 1.07 → 1.04, tracking −0.03em
 * - page:    interior H1 (44–65px below 800px, clamp(48px, 6.5vw, 92px) above); 1.02, −0.035em
 * - feature: homepage H2 (41–45px phones, clamp(42px, 5vw, 72px) above); 1.06, −0.03em
 * - section: interior H2 (36–39px phones, clamp(36px, 4.1vw, 60px) above); 1.06, −0.025em
 * - card:    H3/H4 in cards and steps (28px phones, clamp(27px, 2.5vw, 36px) above); 1.12, −0.015em
 * - minor:   small H3/H4 (24px), e.g. report rows and list heads; 1.12, −0.015em
 */
export const HEADING_SIZES = {
  display:
    'text-[clamp(2.6875rem,11.8vw,3.3125rem)] leading-[1.07] tracking-[-0.03em] sm:text-[clamp(2.9375rem,8.5vw,4.375rem)] nav:text-[clamp(4.375rem,8.8vw,6.3rem)] nav:leading-[1.04] xl:text-[clamp(4.25rem,7.8vw,7.375rem)]',
  page: 'text-[clamp(2.75rem,8.8vw,4.0625rem)] leading-[1.02] tracking-[-0.035em] nav:text-[clamp(3rem,6.5vw,5.75rem)]',
  feature: 'text-[clamp(2.5625rem,12.5vw,2.8125rem)] leading-[1.06] tracking-[-0.03em] nav:text-[clamp(2.625rem,5vw,4.5rem)]',
  section: 'text-[clamp(2.25rem,8vw,2.4375rem)] leading-[1.06] tracking-[-0.025em] nav:text-[clamp(2.25rem,4.1vw,3.75rem)]',
  card: 'text-[1.75rem] leading-[1.12] tracking-[-0.015em] nav:text-[clamp(1.6875rem,2.5vw,2.25rem)]',
  minor: 'text-[1.5rem] leading-[1.12] tracking-[-0.015em]',
};

/** The size each level gets when `size` is not given. */
export const DEFAULT_HEADING_SIZE = { 1: 'page', 2: 'section', 3: 'card', 4: 'minor' };

/**
 * Colour of an `<em>` accent inside the heading (always Faustina italic 500).
 * - inherit: the heading's own colour (interior H1s)
 * - rust:    on light surfaces (5.72:1 on paper; headings are ≥24px, large text)
 * - orange:  on navy only (4.62:1); never on paper/sheet/white
 * - peach:   on navy (9.89:1), the softer accent of the discovery hero
 */
export const HEADING_ACCENTS = {
  inherit: '',
  rust: '[&_em]:text-brand-rust',
  orange: '[&_em]:text-brand-orange',
  peach: '[&_em]:text-brand-peach',
};

/**
 * Hyphenation where a heading would otherwise break mid-word: enlarged text on a phone (an
 * em query, under 20em: under 320px at 16px text, under 640px at 32px). Words then break at
 * a hyphenation point (public/index.html sets lang="en"), and `break-words` stays the last
 * resort. At the default text size the design's line breaks are unchanged (no hyphens).
 */
export const HEADING_HYPHENS = '[@media(max-width:19.9375em)]:hyphens-auto';

/**
 * A Faustina heading, weight 500 (never bold). `level` picks the element (h1–h4), `size`
 * the look, so the outline stays logical whatever the visual size. Children may include
 * `<em>` for the real italic accent: <Heading level={1}>Your next customer <em>out there.</em></Heading>.
 * Colour is inherited from the surface (navy on light, sheet on navy).
 *
 * Props:
 * - `level`: 1 | 2 | 3 | 4 (default 2; anything else falls back to 2)
 * - `size`: 'display' | 'page' | 'feature' | 'section' | 'card' | 'minor'
 *   (default: h1 page, h2 section, h3 card, h4 minor)
 * - `accent`: colour of `<em>` — 'inherit' (default) | 'rust' | 'orange' | 'peach'
 * - `id`, `className`, `children`, other props pass through.
 */
export default function Heading({ level = 2, size, accent = 'inherit', className, children, ...rest }) {
  const lvl = [1, 2, 3, 4].includes(Number(level)) ? Number(level) : 2;
  const Tag = `h${lvl}`;
  const sizeKey = size && HEADING_SIZES[size] ? size : DEFAULT_HEADING_SIZE[lvl];
  return (
    <Tag
      className={cn(
        'break-words font-display font-medium [&_em]:font-display [&_em]:font-medium [&_em]:italic',
        HEADING_HYPHENS,
        HEADING_SIZES[sizeKey],
        HEADING_ACCENTS[accent] ?? '',
        className,
      )}
      {...rest}
    >
      {children}
    </Tag>
  );
}
